import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, downloadFile, authFileUrl } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Printer, Upload, ImageIcon, Loader2, ScanLine } from "lucide-react";

const LEVELS = [
  { v: "raw", l: "Raw" },
  { v: "sanding", l: "Sanding" },
  { v: "polish", l: "Polish" },
  { v: "hardware", l: "Hardware" },
  { v: "final_packing", l: "Final + Packing" },
];
const ST_COLOR = {
  ok: "bg-sage/15 text-sage border-sage/30",
  rework: "bg-ochre/15 text-ochre border-ochre/30",
  reject: "bg-destructive/10 text-destructive border-destructive/30",
  pending: "bg-muted text-muted-foreground border-border",
};
const OVERALL = {
  passed: "border-sage/30 bg-sage/10 text-sage",
  rejected: "border-destructive/30 bg-destructive/10 text-destructive",
  in_progress: "border-[#4A5D6E]/30 bg-[#4A5D6E]/10 text-[#4A5D6E]",
};

export default function QualityChecks() {
  const { user } = useAuth();
  const canQc = ["admin", "manager", "qc"].includes(user?.role);
  const [buyers, setBuyers] = useState([]);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [fBuyer, setFBuyer] = useState("all");
  const [fSku, setFSku] = useState("all");

  const [batchOpen, setBatchOpen] = useState(false);
  const [batch, setBatch] = useState({ buyer_id: "", product_id: "", quantity: 1 });

  const [active, setActive] = useState(null); // article being inspected
  const [scanVal, setScanVal] = useState("");
  const scanLookup = async () => {
    const s = (scanVal || "").trim().split("|")[0];
    if (!s) return;
    const local = articles.find((a) => (a.serial_no || "").toLowerCase() === s.toLowerCase());
    if (local) { setActive(local); setScanVal(""); return; }
    try { const { data } = await api.get(`/qc/articles/by-serial/${encodeURIComponent(s)}`); setActive(data); setScanVal(""); }
    catch (e) { toast.error(`No article with serial ${s}`); }
  };

  const loadArticles = () => {
    const params = {};
    if (fBuyer !== "all") params.buyer_id = fBuyer;
    if (fSku !== "all") params.sku = fSku;
    return api.get("/qc/articles", { params }).then((r) => setArticles(r.data));
  };
  useEffect(() => {
    api.get("/buyers").then((r) => setBuyers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);
  useEffect(() => { loadArticles(); /* eslint-disable-next-line */ }, [fBuyer, fSku]);

  const batchProducts = products.filter((p) => !batch.buyer_id || p.buyer_id === batch.buyer_id);
  const skusInList = [...new Map(articles.map((a) => [a.sku, a])).values()];

  const createBatch = async () => {
    try {
      const res = await api.post("/qc/batches", { product_id: batch.product_id, quantity: Number(batch.quantity) });
      toast.success(`${res.data.count} article(s) serialised`);
      setBatchOpen(false); setBatch({ buyer_id: "", product_id: "", quantity: 1 });
      loadArticles();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeArticle = async (id) => { await api.delete(`/qc/articles/${id}`); toast.success("Deleted"); loadArticles(); };

  return (
    <div className="space-y-8">
      <PageHeader title="Quality Checks" icon={ShieldCheck}
        subtitle="Serialise articles by SKU, inspect Raw → Sanding → Polish → Hardware → Final+Packing with photos."
        action={canQc && (
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="print-labels-button"
              onClick={() => downloadFile(fSku !== "all" ? `/qc/labels.pdf?sku=${fSku}` : "/qc/labels.pdf", "article_labels.pdf")}
              className="gap-2"><Printer className="h-4 w-4" /> Print Labels</Button>
            <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-qc-batch-button" className="gap-2 hover:-translate-y-px"><Plus className="h-4 w-4" /> New QC Batch</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Serialise Articles for QC</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2"><Label>Buyer</Label>
                    <Select value={batch.buyer_id} onValueChange={(v) => setBatch({ ...batch, buyer_id: v, product_id: "" })}>
                      <SelectTrigger data-testid="qc-batch-buyer"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                      <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>SKU / Product</Label>
                    <Select value={batch.product_id} onValueChange={(v) => setBatch({ ...batch, product_id: v })}>
                      <SelectTrigger data-testid="qc-batch-sku"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                      <SelectContent>
                        {batchProducts.length === 0 && <SelectItem value="none" disabled>No products for this buyer</SelectItem>}
                        {batchProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku || "(no sku)"} · {p.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Quantity (labels to generate)</Label>
                    <Input type="number" min="1" data-testid="qc-batch-qty" value={batch.quantity}
                      onChange={(e) => setBatch({ ...batch, quantity: e.target.value })} /></div>
                  <p className="text-xs text-muted-foreground">Each unit gets a unique serial (e.g. SKU-0001) tracked across all 5 QC levels.</p>
                </div>
                <DialogFooter><Button data-testid="qc-batch-save" onClick={createBatch} disabled={!batch.product_id}>Generate Serials</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )} />

      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Input data-testid="qc-scan-input" placeholder="Scan / enter serial…" value={scanVal}
            onChange={(e) => setScanVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") scanLookup(); }} className="w-52" />
          <Button variant="outline" data-testid="qc-scan-go" onClick={scanLookup} className="gap-1"><ScanLine className="h-4 w-4" /> Inspect</Button>
        </div>
        <Select value={fBuyer} onValueChange={setFBuyer}>
          <SelectTrigger className="w-56" data-testid="qc-filter-buyer"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All buyers</SelectItem>
            {buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fSku} onValueChange={setFSku}>
          <SelectTrigger className="w-56" data-testid="qc-filter-sku"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All SKUs</SelectItem>
            {skusInList.map((a) => <SelectItem key={a.sku} value={a.sku}>{a.sku} · {a.product_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {articles.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No articles yet" hint="Create a QC batch to serialise articles and start inspecting." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Serial</TableHead><TableHead>Product</TableHead><TableHead>Buyer</TableHead>
                <TableHead>Levels</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.map((a) => (
                <TableRow key={a.id} data-testid={`qc-row-${a.id}`} className="hover:bg-muted/40">
                  <TableCell className="font-semibold">{a.serial_no}</TableCell>
                  <TableCell>{a.product_name}<span className="ml-1 text-xs text-muted-foreground">{a.category}</span></TableCell>
                  <TableCell className="text-muted-foreground">{a.buyer_name || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {LEVELS.map((lv) => {
                        const s = a.stages?.[lv.v]?.status || "pending";
                        return <span key={lv.v} title={`${lv.l}: ${s}`}
                          className={`h-2.5 w-2.5 rounded-full ${s === "ok" ? "bg-sage" : s === "reject" ? "bg-destructive" : s === "rework" ? "bg-ochre" : "bg-border"}`} />;
                      })}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-full text-[11px] font-semibold capitalize ${OVERALL[a.overall_status]}`}>
                      {a.overall_status.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="outline" size="sm" data-testid={`qc-open-${a.id}`} onClick={() => setActive(a)}>Inspect</Button>
                      <Button variant="ghost" size="icon" data-testid={`qc-label-${a.id}`} title="Print label"
                        onClick={() => downloadFile(`/qc/labels.pdf?ids=${a.id}`, `${a.serial_no}.pdf`)}
                        className="h-8 w-8 text-muted-foreground hover:text-primary"><Printer className="h-4 w-4" /></Button>
                      {["admin", "manager"].includes(user?.role) && (
                        <Button variant="ghost" size="icon" data-testid={`qc-delete-${a.id}`} onClick={() => removeArticle(a.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ArticleDialog article={active} canQc={canQc} onClose={() => setActive(null)}
        onChanged={(updated) => { setActive(updated); setArticles((prev) => prev.map((x) => (x.id === updated.id ? updated : x))); }} />
    </div>
  );
}

function ArticleDialog({ article, canQc, onClose, onChanged }) {
  if (!article) return null;
  return (
    <Dialog open={!!article} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{article.serial_no}
            <span className="ml-2 text-sm font-normal text-muted-foreground">{article.product_name} · {article.buyer_name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
          {LEVELS.map((lv) => (
            <LevelBlock key={lv.v} article={article} level={lv} canQc={canQc} onChanged={onChanged} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LevelBlock({ article, level, canQc, onChanged }) {
  const stage = article.stages?.[level.v] || { status: "pending", remarks: "", photos: [] };
  const [status, setStatus] = useState(stage.status);
  const [remarks, setRemarks] = useState(stage.remarks || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setStatus(stage.status); setRemarks(stage.remarks || ""); /* eslint-disable-next-line */ }, [article.id, level.v]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/qc/articles/${article.id}/stage`, { level: level.v, status, remarks });
      toast.success(`${level.l} updated`);
      onChanged(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/qc/articles/${article.id}/stage/${level.v}/photo`, fd);
      }
      const { data } = await api.get(`/qc/articles`, { params: { sku: article.sku } });
      const updated = data.find((x) => x.id === article.id);
      if (updated) onChanged(updated);
      toast.success("Photo(s) uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const photos = stage.photos || [];
  return (
    <div className="rounded-md border border-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="font-heading text-sm font-bold">{level.l}</p>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${ST_COLOR[stage.status] || ST_COLOR.pending}`}>{stage.status}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs">Result</Label>
          <Select value={status} onValueChange={setStatus} disabled={!canQc}>
            <SelectTrigger data-testid={`qc-${level.v}-status`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="ok">OK</SelectItem>
              <SelectItem value="rework">Rework</SelectItem>
              <SelectItem value="reject">Reject</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Remarks</Label>
          <Input data-testid={`qc-${level.v}-remarks`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canQc} placeholder="Notes / defects" />
        </div>
      </div>

      {photos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <a key={i} href={authFileUrl(p.path)} target="_blank" rel="noreferrer">
              <img src={authFileUrl(p.path)} alt="qc" className="h-16 w-16 rounded-md border border-border object-cover" />
            </a>
          ))}
        </div>
      )}

      {canQc && (
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid={`qc-${level.v}-save`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Add photos
            <input type="file" accept="image/*" multiple className="hidden" data-testid={`qc-${level.v}-photo`} onChange={upload} disabled={uploading} />
          </label>
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><ImageIcon className="h-3 w-3" /> {photos.length}</span>
        </div>
      )}
    </div>
  );
}
